import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { createCachedObservable } from '../utils/cached-observable.util';
import type {
  Cuenta,
  CuentaDetalle,
  CuentasResponse,
  CuentasBasicoResponse,
  CuentasParams,
  CreateCuentaPayload,
  UpdateDatosPagoPayload,
  UpdateCuentaPayload,
  CreateContactoPayload,
  UpdateContactoPayload,
} from '../models/cuenta.model';
import { normalizeCuentaDistribuidor } from '../utils/cuenta-distribuidor.util';
import type { PanelResponse } from '../models/panel.model';
import type { CuentaAlertasPayload } from '../models/cuenta-alertas.model';
import type {
  AccesosResponse,
  ApiClientEndpointResponse,
  EditUserApiSuccessResponse,
  EnableApiSuccessResponse,
  UsuariosAccesoResponse,
  UsuarioAccesoPayload,
} from '../models/accesos.model';

@Injectable({ providedIn: 'root' })
export class CuentasService {
  private readonly baseUrl = `${environment.apiUrl}/api`;

  private readonly cuentasBasicoCache = createCachedObservable(() =>
    this.http.get<CuentasBasicoResponse>(`${this.baseUrl}/cuentas/basico`),
  );

  constructor(private http: HttpClient) {}

  /** Respuesta `{ data: T }` o cuerpo plano; si existe la clave `data`, se devuelve su valor (como getCuenta). */
  private unwrapDataKey<T>(raw: { data?: T } | T): T {
    return raw !== null && typeof raw === 'object' && 'data' in raw
      ? (raw as { data: T }).data
      : (raw as T);
  }

  /** Preferir `data` cuando viene definido y no es null. */
  private unwrapNonNullData<T>(raw: { data?: T | null } | T): T {
    if (raw != null && typeof raw === 'object' && 'data' in raw) {
      const d = (raw as { data?: T | null }).data;
      if (d != null) return d;
    }
    return raw as T;
  }

  private buildCuentasHttpParams(params: CuentasParams): HttpParams {
    let httpParams = new HttpParams();
    if (params.page != null) httpParams = httpParams.set('page', params.page.toString());
    if (params.per_page != null) httpParams = httpParams.set('per_page', params.per_page.toString());
    if (params.search?.trim()) httpParams = httpParams.set('search', params.search.trim());
    if (params.tipo_cliente?.trim()) httpParams = httpParams.set('tipo_cliente', params.tipo_cliente.trim());
    if (params.fecha_insert_from) httpParams = httpParams.set('fecha_insert_from', params.fecha_insert_from);
    if (params.fecha_insert_until) httpParams = httpParams.set('fecha_insert_until', params.fecha_insert_until);
    if (params.origen?.trim()) httpParams = httpParams.set('origen', params.origen.trim());
    if (params.id_agente != null) httpParams = httpParams.set('id_agente', params.id_agente.toString());
    if (params.id_partner != null) httpParams = httpParams.set('id_partner', params.id_partner.toString());
    if (params.id_mercado != null) httpParams = httpParams.set('id_mercado', params.id_mercado.toString());
    if (params.id_region != null) httpParams = httpParams.set('id_region', params.id_region.toString());
    if (params.id_pais != null) httpParams = httpParams.set('id_pais', params.id_pais.toString());
    if (params.is_internal != null) httpParams = httpParams.set('is_internal', params.is_internal.toString());
    if (params.show_only_clients != null) {
      httpParams = httpParams.set('show_only_clients', params.show_only_clients.toString());
    }
    if (params.distribuidor != null) {
      httpParams = httpParams.set('distribuidor', params.distribuidor.toString());
    }
    if (params.sort_by?.trim()) {
      httpParams = httpParams.set('sort_by', params.sort_by.trim());
    }
    if (params.sort_dir?.trim()) {
      httpParams = httpParams.set('sort_dir', params.sort_dir.trim());
    }
    return httpParams;
  }

  /** Obtiene una cuenta por id. GET /api/cuentas/:id (o /api/cuenta/:id según backend). */
  getCuenta(id: number): Observable<CuentaDetalle> {
    return this.http
      .get<{ data: CuentaDetalle } | CuentaDetalle>(`${this.baseUrl}/cuentas/${id}`)
      .pipe(map((res) => normalizeCuentaDistribuidor(this.unwrapDataKey(res))));
  }

  getCuentas(params: CuentasParams = {}): Observable<CuentasResponse> {
    return this.http.get<CuentasResponse>(`${this.baseUrl}/cuentas`, {
      params: this.buildCuentasHttpParams(params),
    }).pipe(
      map((res) => ({
        ...res,
        data: (res.data ?? []).map((cuenta) => normalizeCuentaDistribuidor(cuenta)),
      })),
    );
  }

  /** Listado mínimo de todas las cuentas. GET /api/cuentas/basico (caché en memoria). */
  getCuentasBasico(): Observable<CuentasBasicoResponse> {
    return this.cuentasBasicoCache.get();
  }

  /** Invalida la caché de cuentas básicas (p. ej. tras logout). */
  clearCuentasBasicoCache(): void {
    this.cuentasBasicoCache.clear();
  }

  /** Crea una cuenta. POST /api/cuentas */
  createCuenta(payload: CreateCuentaPayload): Observable<{ data: Cuenta } | Cuenta> {
    const body: Record<string, unknown> = {
      nombre_comercial: payload.nombre_comercial.trim(),
    };
    if (payload.origen != null) {
      body['origen'] = payload.origen;
    }
    if (payload.id_agente != null && (payload.origen === 'agente' || payload.origen == null)) {
      body['id_agente'] = payload.id_agente;
    }
    if (payload.origen === 'partner' && payload.id_partner != null) {
      body['id_partner'] = payload.id_partner;
    }
    if (payload.id_pais != null) {
      body['id_pais'] = payload.id_pais;
    }
    if (payload.distribuidor != null) {
      body['distribuidor'] = payload.distribuidor;
    }
    return this.http.post<{ data: Cuenta } | Cuenta>(`${this.baseUrl}/cuentas`, body);
  }

  /** Convierte un prospecto en cliente. PUT /api/cuentas/:id (con payload completo). */
  updateCuenta(idCuenta: number, payload: UpdateCuentaPayload): Observable<unknown> {
    return this.http.put<unknown>(`${this.baseUrl}/cuentas/${idCuenta}`, payload);
  }

  /** Actualiza los datos de pago de una cuenta. PUT /api/cuentas/:id */
  updateDatosPago(idCuenta: number, payload: UpdateDatosPagoPayload): Observable<CuentaDetalle | unknown> {
    return this.http.put<CuentaDetalle | unknown>(`${this.baseUrl}/cuentas/${idCuenta}`, payload);
  }

  /** Actualiza los datos generales de una cuenta. PUT /api/cuentas/:id */
  patchCuenta(idCuenta: number, payload: UpdateCuentaPayload): Observable<CuentaDetalle | unknown> {
    return this.http.put<CuentaDetalle | unknown>(`${this.baseUrl}/cuentas/${idCuenta}`, payload);
  }

  /** Elimina una cuenta. DELETE /api/cuentas/:id */
  deleteCuenta(idCuenta: number): Observable<unknown> {
    return this.http.delete<unknown>(`${this.baseUrl}/cuentas/${idCuenta}`);
  }

  /** Datos del panel de la cuenta. GET /api/cuentas/:id/panel */
  getPanel(idCuenta: number): Observable<PanelResponse> {
    return this.http.get<PanelResponse>(`${this.baseUrl}/cuentas/${idCuenta}/panel`);
  }

  /** Alertas comerciales de la cuenta. GET /api/cuentas/:id/alertas */
  getAlertas(idCuenta: number): Observable<CuentaAlertasPayload> {
    return this.http
      .get<{ data: CuentaAlertasPayload } | CuentaAlertasPayload>(
        `${this.baseUrl}/cuentas/${idCuenta}/alertas`,
      )
      .pipe(map((res) => this.unwrapDataKey(res)));
  }

  /** Licencias de acceso de la cuenta. GET /api/accesos/:idCuenta */
  getAccesos(idCuenta: number): Observable<AccesosResponse> {
    return this.http.get<AccesosResponse>(`${this.baseUrl}/cuentas/${idCuenta}/accesos`);
  }

  /** Crea un contacto para una cuenta. POST /api/contactos */
  createContacto(payload: CreateContactoPayload): Observable<unknown> {
    return this.http.post<unknown>(`${this.baseUrl}/contactos`, payload);
  }

  /** Actualiza un contacto. PUT /api/contactos/:id */
  updateContacto(idContacto: number, payload: UpdateContactoPayload): Observable<unknown> {
    return this.http.put<unknown>(`${this.baseUrl}/contactos/${idContacto}`, payload);
  }

  /** Elimina un contacto. DELETE /api/contactos/:id */
  deleteContacto(idContacto: number): Observable<unknown> {
    return this.http.delete<unknown>(`${this.baseUrl}/contactos/${idContacto}`);
  }

  /** Usuarios de configuración de acceso. GET /api/cuentas/:idCuenta/usuarios-acceso */
  getUsuariosAcceso(idCuenta: number): Observable<UsuariosAccesoResponse> {
    return this.http.get<UsuariosAccesoResponse>(`${this.baseUrl}/cuentas/${idCuenta}/usuarios-acceso`);
  }

  /** Añade un usuario de configuración de acceso. POST /api/cuentas/:idCuenta/usuarios-acceso */
  addUsuarioAcceso(idCuenta: number, payload: UsuarioAccesoPayload): Observable<unknown> {
    return this.http.post<unknown>(`${this.baseUrl}/cuentas/${idCuenta}/accesos`, payload);
  }

  /** Actualiza un usuario de configuración de acceso. PUT /api/cuentas/:idCuenta/usuarios-acceso/:idUser */
  updateUsuarioAcceso(idCuenta: number, idUser: number, payload: UsuarioAccesoPayload): Observable<unknown> {
    return this.http.put<unknown>(`${this.baseUrl}/cuentas/${idCuenta}/accesos/${idUser}`, payload);
  }

  /**
   * Segmento de ruta con access_code opcional:
   * …/family/:familyId[/access/:accessCode]/suffix
   */
  private familyAccessPath(
    idCuenta: number,
    userIdentifier: string | number,
    familyId: string | number,
    accessCode: string | null | undefined,
    suffix: string,
  ): string {
    const idSeg = encodeURIComponent(String(userIdentifier));
    const base = `${this.baseUrl}/cuentas/${idCuenta}/accesos/${idSeg}/family/${familyId}`;
    const code = accessCode != null ? String(accessCode).trim() : '';
    if (code) {
      return `${base}/access/${encodeURIComponent(code)}${suffix}`;
    }
    return `${base}${suffix}`;
  }

  /**
   * Provisiona el acceso del usuario a una familia (y opcionalmente a un access_code).
   * POST …/family/:familyId[/access/:accessCode]/provision
   */
  provisionUsuarioAcceso(
    idCuenta: number,
    userIdentifier: string | number,
    familyId: string | number,
    options?: { is_admin: boolean; password?: string; accessCode?: string | null },
  ): Observable<unknown> {
    const pwd = options?.password != null ? String(options.password).trim() : '';
    const body: { is_admin: boolean; password?: string } = {
      is_admin: options?.is_admin ?? false,
    };
    if (pwd) {
      body.password = pwd;
    }
    return this.http.post<unknown>(
      this.familyAccessPath(idCuenta, userIdentifier, familyId, options?.accessCode, '/provision'),
      body,
    );
  }

  /**
   * Deshabilita el usuario en la familia (y opcionalmente en un access_code).
   * POST …/family/:familyId[/access/:accessCode]/disable-user
   */
  disableUsuarioAcceso(
    idCuenta: number,
    userIdentifier: string | number,
    familyId: string | number,
    accessCode?: string | null,
  ): Observable<unknown> {
    return this.http.post<unknown>(
      this.familyAccessPath(idCuenta, userIdentifier, familyId, accessCode, '/disable-user'),
      {},
    );
  }

  /** Elimina un usuario de configuración de acceso. DELETE /api/cuentas/:idCuenta/usuarios-acceso/:idUser */
  deleteUsuarioAcceso(idCuenta: number, idUser: number): Observable<unknown> {
    return this.http.delete<unknown>(`${this.baseUrl}/cuentas/${idCuenta}/usuarios-acceso/${idUser}`);
  }

  /**
   * Deep-link SSO. Con accessCode: GET …/family/:id/access/:code; sin él: ruta legacy.
   */
  accessSSO(
    idCuenta: number,
    sharedEntityId: number,
    familiaId: string | null,
    accessCode?: string | null,
  ): Observable<unknown> {
    const code = accessCode != null ? String(accessCode).trim() : '';
    if (code) {
      return this.http.get<unknown>(
        `${this.baseUrl}/cuentas/${idCuenta}/accesos/${sharedEntityId}/family/${familiaId}/access/${encodeURIComponent(code)}`,
      );
    }
    return this.http.get<unknown>(
      `${this.baseUrl}/cuentas/${idCuenta}/accesos/${sharedEntityId}/family/${familiaId}`,
    );
  }

  /** Base path: POST/GET .../cuentas/:id/families/:family_code/api/... */
  private familiesApiPath(idCuenta: number, familyCode: string): string {
    return `${this.baseUrl}/cuentas/${idCuenta}/families/${encodeURIComponent(familyCode)}/api`;
  }

  /**
   * Activa la API para la familia de producto (genera/habilita la clave).
   * POST /api/cuentas/:cuenta/families/:family_code/api/enable
   * Respuesta: username, api_key, pass (y opcionalmente envuelto en `data`).
   */
  enableApi(
    idCuenta: number,
    familyCode: string,
    payload: { shared_entity_id: number },
  ): Observable<EnableApiSuccessResponse> {
    return this.http
      .post<{ data?: EnableApiSuccessResponse } | EnableApiSuccessResponse>(
        `${this.familiesApiPath(idCuenta, familyCode)}/enable`,
        payload,
      )
      .pipe(map((raw) => this.unwrapNonNullData<EnableApiSuccessResponse>(raw)));
  }

  /**
   * Asocia o actualiza un usuario respecto a la API key (p. ej. añadir o quitar).
   * POST /api/cuentas/:cuenta/families/:family_code/api/edit-user
   * En add, respuesta típica: msg, username, pass, url (la api_key la tiene el cliente en estado de la familia).
   */
  editUserApi(
    idCuenta: number,
    familyCode: string,
    userId: number,
    remove = false,
  ): Observable<EditUserApiSuccessResponse> {
    const body = remove
      ? { shared_entity_id: userId, action: 'remove' }
      : { shared_entity_id: userId, action: 'add' };
    return this.http
      .post<{ data?: EditUserApiSuccessResponse } | EditUserApiSuccessResponse>(
        `${this.familiesApiPath(idCuenta, familyCode)}/edit-user`,
        body,
      )
      .pipe(
        map((raw) => {
          const merged = this.unwrapNonNullData<EditUserApiSuccessResponse>(raw);
          return {
            msg: merged.msg,
            username: merged.username ?? '',
            pass: merged.pass,
            url: merged.url,
          };
        }),
      );
  }

  /**
   * Elimina la API key de la familia.
   * POST /api/cuentas/:cuenta/families/:family_code/api/delete-key
   */
  deleteApiKey(idCuenta: number, familyCode: string): Observable<unknown> {
    return this.http.post<unknown>(`${this.familiesApiPath(idCuenta, familyCode)}/delete-key`, {});
  }

  /**
   * URL del endpoint de cliente para consumir la API.
   * GET /api/cuentas/:cuenta/families/:family_code/api/client-endpoint
   */
  getApiClientEndpoint(idCuenta: number, familyCode: string): Observable<ApiClientEndpointResponse> {
    return this.http
      .get<{ data: ApiClientEndpointResponse } | ApiClientEndpointResponse>(
        `${this.familiesApiPath(idCuenta, familyCode)}/client-endpoint`,
      )
      .pipe(
        map(
          (res): ApiClientEndpointResponse =>
            this.unwrapNonNullData<ApiClientEndpointResponse>(res),
        ),
      );
  }

  /**
   * Cambia la contraseña del usuario de la API key.
   * POST /cuentas/:idCuenta/accesos/:sharedEntityId/family/:familyId/reset-password
   * Body: `{ email }` del usuario API cuya contraseña se resetea.
   * @returns Observable con `{ pass, email }` (y alias legacy password / new_password)
   * Errores: manejar en subscribe (error callback).
   */
  resetPassword(
    idCuenta: number,
    sharedEntityId: number,
    familyId: string | number,
    email: string,
  ): Observable<{ pass?: string; email?: string; password?: string; new_password?: string }> {
    return this.http.post<{ pass?: string; email?: string; password?: string; new_password?: string }>(
      `${this.baseUrl}/cuentas/${idCuenta}/accesos/${sharedEntityId}/family/${familyId}/reset-password`,
      { email },
    );
  }

  /**
   * Bloquea/desbloquea la compañía para accesos.
   * POST /cuentas/:idCuenta/accesos/block-company
   */
  blockCompany(idCuenta: number, block: boolean): Observable<unknown> {
    return this.http.post<unknown>(`${this.baseUrl}/cuentas/${idCuenta}/accesos/block-company`, { block });
  }

  /**
   * Recalcula el balance de accesos de la cuenta.
   * POST /cuentas/:idCuenta/accesos/recalculate-balance
   */
  recalculateAccessBalance(idCuenta: number): Observable<unknown> {
    return this.http.post<unknown>(`${this.baseUrl}/cuentas/${idCuenta}/accesos/recalculate-balance`, {});
  }
}
